import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, CalculatorIcon, ClipboardCheckIcon, FileTextIcon, FolderSearchIcon, LayersIcon, LayoutGridIcon, TargetIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/cab4e950-4b96-4eb9-b46e-987af20ac416/1792619608477_matteklar-mockup-estandar-caja-6-bind-transparente-bd55bd2b.webp';
export const heroGuarantees = ['Umiddelbar digital nedlasting', 'Engangsbetaling', 'Ingen abonnement', '7 dagers fornøydgaranti'];
export const stats = [{
  value: '240',
  label: 'Sider i hovedbindene'
}, {
  value: '6',
  label: 'Digitale bind'
}, {
  value: '10',
  label: 'Trinn – fra 1. til 10.'
}, {
  value: '5',
  label: 'Bonuser inkludert'
}, {
  value: '3',
  label: 'Nivåer på arbeidsarkene'
}];
export const differenceStats = [{
  value: '1.–10. trinn',
  label: 'Fra barneskole til ungdomsskole'
}, {
  value: '6 digitale bind',
  label: 'Fra tallforståelse og geometri til algebra, funksjoner og eksamen'
}, {
  value: 'PDF klar til utskrift',
  label: 'Bruk det digitalt eller skriv ut til klasserommet'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Kvelder og helger forsvinner',
  text: 'Timer foran skjermen for å planlegge neste ukes matematikktimer – og du er fortsatt ikke ferdig.'
}, {
  title: 'Timer med leting etter oppgaver',
  text: 'Oppgaver fra ulike nettsider er for enkle, for vanskelige eller må tilpasses før de fungerer i klassen.'
}, {
  title: 'Klasser med store nivåforskjeller',
  text: 'Noen elever trenger konkreter og støtte, andre er ferdige på fem minutter – og timen skal fungere for alle.'
}, {
  title: 'Elever som henger etter',
  text: 'Du ser at noen ikke har knekt plassverdi, brøk eller likninger – men har ikke tid til å lage nytt opplegg.'
}, {
  title: 'Vanskelig å vurdere og dokumentere',
  text: 'Kompetansemål, underveisvurdering, nasjonale prøver og eksamen – hvordan begrunner og dokumenterer du det?'
}, {
  title: 'Stress når timen begynner',
  text: 'Når tiden ikke strekker til, blir det fort «gjør oppgave 1 til 20 i boka» og improvisasjon i siste liten.'
}];
export const afterItems = ['En ferdig årsplan for 1.–10. trinn', '12 ferdige økter minutt for minutt', 'Materiell og lærerinstruks til hver økt', 'Arbeidsark i 3 nivåer', 'Fasit til alt', 'Koblet til kompetansemål i LK20', 'Problemløsning og hverdagsmatematikk', 'Prøver, vurderingskriterier og ferdige tilbakemeldinger', 'PDF klar til utskrift'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Kveldene går med til planlegging',
  text: 'Bruker du kvelder og helger på å planlegge neste ukes matematikktimer?'
}, {
  icon: FolderSearchIcon,
  title: 'Du leter etter oppgaver som passer',
  text: 'Leter du hele tiden på nettet etter oppgaver som passer trinnet og kompetansemålene?'
}, {
  icon: UsersIcon,
  title: 'Klassen har store nivåforskjeller',
  text: 'Trenger du oppgaver som fungerer både for elever som trenger støtte og for dem som vil ha utfordringer?'
}, {
  icon: TargetIcon,
  title: 'Noen elever henger etter',
  text: 'Ser du elever som mangler grunnleggende tallforståelse, men har ikke tid til å lage ekstra opplegg?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Vurderingen tar tid',
  text: 'Vil du ha ferdige prøver, kriterier og tilbakemeldinger – og forberede elevene på nasjonale prøver og eksamen?'
}, {
  icon: LayoutGridIcon,
  title: 'Du trenger en tydeligere struktur',
  text: 'Vil du ha alt samlet, slik at du raskt finner riktig økt når timen begynner?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Bind 01',
  level: '1.–4. trinn',
  title: 'Tall og regning',
  tagline: 'Vit nøyaktig hva hver time skal handle om – hele skoleåret.',
  intro: 'Det første bindet gir deg en komplett årsplan for 1.–4. trinn og tolv ferdige økter som du kan gjennomføre med en gang.',
  items: ['Årsplan for 1.–4. trinn', '38 skoleuker koblet til kompetansemål', '12 ferdige økter minutt for minutt', 'Økter på 45 og 60 minutter', 'Materiell og lærerinstruks til hver økt', 'Tallforståelse 0–1000 og plassverdi', 'Addisjon, subtraksjon, gangetabellen og divisjon', 'Mønstre og hoderegning', 'Arbeidsark i 3 nivåer med fasit'],
  outro: 'Ferdige økter som gjør at du går inn i timen med en tydelig plan.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 02',
  level: '1.–4. trinn',
  title: 'Geometri, måling & statistikk',
  tagline: 'Gjør matematikken konkret og synlig.',
  intro: 'Det andre bindet tar elevene med fra former og figurer til måling, klokka, penger og enkle diagrammer.',
  items: ['Former og figurer', 'Symmetri', 'Lengde, masse, volum og tid', 'Klokka og penger (kroner)', 'Tabeller og søylediagram', 'Problemløsning med konkreter', 'Arbeidsark i 3 nivåer', 'Spill og fasit'],
  outro: 'Praktisk matematikk som elevene kan ta og føle på.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 03',
  level: '5.–7. trinn',
  title: 'Tall, brøk & prosent',
  tagline: 'Bygg en solid forståelse av tall – også de vanskelige.',
  intro: 'Det tredje bindet gir elevene sammenhengen mellom store tall, desimaltall, brøk og prosent.',
  items: ['Store tall og posisjonssystemet', 'Desimaltall', 'Brøk', 'Prosent', 'Sammenhengen mellom brøk, desimaltall og prosent', 'Overslag', 'Hverdagsøkonomi', 'Ferdige økter, arbeidsark i 3 nivåer og fasit'],
  outro: 'Tydelige økter som gjør brøk og prosent forståelig for alle.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 04',
  level: '5.–7. trinn',
  title: 'Algebra, geometri & statistikk',
  tagline: 'Fra mønstre til likninger, vinkler og sannsynlighet.',
  intro: 'Det fjerde bindet introduserer algebraisk tenkning og utvider geometri og statistikk på mellomtrinnet.',
  items: ['Mønstre og likninger', 'Uttrykk', 'Koordinatsystemet', 'Vinkler', 'Areal og omkrets', 'Gjennomsnitt, median og typetall', 'Sannsynlighet', 'Ferdige økter, arbeidsark og fasit'],
  outro: 'En praktisk støtte for å lære bort abstrakte begreper på en konkret måte.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 05',
  level: '8.–10. trinn',
  title: 'Tall, algebra & funksjoner',
  tagline: 'Ungdomstrinnets kjerne – ferdig planlagt.',
  intro: 'Det femte bindet dekker tall, algebra og funksjoner på ungdomstrinnet – med programmering og privatøkonomi.',
  items: ['Negative tall', 'Potenser og kvadratrøtter', 'Prosent og vekstfaktor', 'Likninger og ulikheter', 'Lineære funksjoner', 'Programmering og algoritmisk tenkning', 'Økonomi: budsjett, lån og rente', 'Arbeidsark i 3 nivåer og fasit'],
  outro: 'Ferdige økter som gjør algebra og funksjoner håndterlig for hele klassen.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 06',
  level: '8.–10. trinn',
  title: 'Geometri, statistikk, vurdering & eksamen',
  tagline: 'Vis hva elevene kan – og gjør dem klare til eksamen.',
  intro: 'Det sjette bindet samler geometri og statistikk med verktøyene for vurdering, nasjonale prøver og eksamen.',
  items: ['Pytagoras og formlikhet', 'Areal og volum', 'Statistikk og sannsynlighet', 'Underveisvurdering', 'Vurderingskriterier i 3 nivåer', 'Prøver A/B med fasit', 'Forberedelse til nasjonale prøver i regning og eksamen 10. trinn', '100 ferdige tilbakemeldinger'],
  outro: 'En komplett støtte for å vurdere rettferdig og forberede elevene trygt.',
  note: 'Materialet er selvstendig utviklet og er ikke et offisielt produkt fra Utdanningsdirektoratet.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitale bind i matematikk',
  text: 'Et komplett undervisningssystem som hjelper deg å planlegge og gjennomføre matematikken hele skoleåret. Hvert bind har 40 sider med ferdige økter, arbeidsark i 3 nivåer og fasit.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Ferdige økter minutt for minutt',
  text: 'Slipp å begynne på et tomt ark. Tydelige opplegg med tider, materiell og lærerinstruks hjelper deg å komme raskt i gang.'
}, {
  icon: FileTextIcon,
  title: 'Arbeidsark i 3 nivåer',
  text: 'Samme mål i tre nivåer – støtte, basis og utfordring – slik at alle elever kan jobbe med det samme.'
}, {
  icon: CalculatorIcon,
  title: 'Fasit til alt',
  text: 'Fasit til arbeidsark, oppgaver og prøver, slik at du sparer tid på retting.'
}, {
  icon: LayersIcon,
  title: 'Koblet til LK20',
  text: 'Kjerneelementene og kompetansemålene i matematikk for 1.–10. trinn – tydelig koblet til øktene.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Vurdering & eksamen',
  text: 'Vurderingskriterier, prøver A/B, forberedelse til nasjonale prøver og eksamen og 100 ferdige tilbakemeldinger.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årsplan',
  text: 'Støtte for å organisere hele skoleåret og velge temaer ut fra trinnet og klassen.'
}];
// Real pages from Bind 1 made in Gamma Studio (gamma zx0lzj7x6nh36rx, PDF rendered at 3.5x). While empty, InsidePages shows a neutral placeholder.
const PAGE_BASE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/cab4e950-4b96-4eb9-b46e-987af20ac416/';
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [{
  src: `${PAGE_BASE}1792621381788_mk-bind1-side01-0353b105.webp`,
  label: 'Bind 1 · Side 1',
  title: 'Tall og regning · 1.–4. trinn',
  text: 'Hvert bind starter med en tydelig oversikt over temaene og hva som er inkludert.'
}, {
  src: `${PAGE_BASE}1792621385518_mk-bind1-side07-2e15ca62.webp`,
  label: 'Bind 1 · Side 7',
  title: 'Årsplan 2. trinn – 38 skoleuker',
  text: 'Hele skoleåret uke for uke, med tema for høst og vår og hvilke økter som hører til.'
}, {
  src: `${PAGE_BASE}1792621389405_mk-bind1-side12-d4a2067b.webp`,
  label: 'Bind 1 · Side 12',
  title: 'Økt 1: Tallvenner til 10',
  text: 'En ferdig økt på 45 minutter: mål, materiell, minutt for minutt og lærerinstruks.'
}, {
  src: `${PAGE_BASE}1792621393316_mk-bind1-side13-137dc520.webp`,
  label: 'Bind 1 · Side 13',
  title: 'Arbeidsark: Tallvenner til 10',
  text: 'Samme mål i tre nivåer – støtte, basis og utfordring – med fasit nederst.'
}, {
  src: `${PAGE_BASE}1792621397157_mk-bind1-side38-26c9b51e.webp`,
  label: 'Bind 1 · Side 38',
  title: 'Fasit til prøvene',
  text: 'Fasit til prøvene for 1.–2. og 3.–4. trinn, slik at du sparer tid på retting.'
}];
export const pageTypes = ['Ferdige økter', 'Arbeidsark i 3 nivåer', 'Fasit', 'Vurdering'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Årsplan i matematikk – ukeplaner for 1.–10. trinn',
  text: 'Ferdige ukeplaner for hele skoleåret, slik at du alltid vet hva neste uke skal handle om.',
  items: ['Ukeplaner for 1.–10. trinn', '38 skoleuker', 'Koblet til kompetansemål', 'Klar til utskrift'],
  benefit: 'Hele året på plass – før det har begynt.'
}, {
  title: '100 ekstra arbeidsark i matematikk',
  text: 'Ekstra øving til elever som trenger mer, som er ferdige før de andre, eller til lekser.',
  items: ['100 arbeidsark', 'Alle trinn', 'Ulike nivåer', 'Med fasit'],
  benefit: 'Alltid noe meningsfullt å gi elevene.'
}, {
  title: 'Matematikkspill & aktivitetskort',
  text: 'Spill og aktivitetskort som gjør øving og repetisjon aktivt og morsomt.',
  items: ['Spill for hele klassen', 'Aktivitetskort', 'Hoderegning og tallforståelse', 'Klar til utskrift'],
  benefit: 'Mer motivasjon – mindre mas.'
}, {
  title: 'Problemløsningsbanken – 60 hverdagsoppgaver med fasit',
  text: 'Problemløsningsoppgaver fra hverdagen som trener resonnering, modellering og argumentasjon.',
  items: ['60 hverdagsoppgaver', 'Ulike vanskelighetsgrader', 'Løsningsforslag og fasit', 'Koblet til kjerneelementene'],
  benefit: 'Matematikk elevene kjenner igjen fra livet.'
}, {
  title: 'Vikarpakken – 12 ferdige matematikktimer',
  text: 'Tolv ferdige timer som en vikar kan gjennomføre uten forberedelse.',
  items: ['12 ferdige timer', 'Instruksjon til vikaren', 'Arbeidsark med fasit', 'Klar til utskrift'],
  benefit: 'Ingen bortkastede timer når du er borte.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Åpne MATTEKLAR™',
  text: 'Last ned pakken og åpne bindet som passer trinnet ditt.'
}, {
  title: 'Velg trinn og økt',
  text: 'Finn riktig økt, tema eller arbeidsark for klassen din.'
}, {
  title: 'Forbered deg på 5 minutter',
  text: 'Les gjennom lærerinstruksen og gjør klart materiellet du trenger.'
}, {
  title: 'Skriv ut og start timen',
  text: 'Skriv ut arbeidsarkene eller bruk materialet digitalt – og gå inn i timen med en tydelig plan.'
}];
export const audienceItems = ['Underviser i matematikk på 1.–10. trinn', 'Er kontaktlærer og har matematikk på timeplanen', 'Vil ha en ferdig årsplan koblet til LK20', 'Har klasser med store nivåforskjeller', 'Vil slippe å planlegge hver time fra bunnen av', 'Vil ha arbeidsark i 3 nivåer med fasit', 'Vil forberede elevene på nasjonale prøver og eksamen', 'Vil spare kvelder og helger på planlegging'];
export const timeValueTasks = ['Lete etter oppgaver på nettet', 'Planlegge øktene', 'Lage arbeidsark i flere nivåer', 'Lage fasit og prøver', 'Vurdere og gi tilbakemeldinger'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitale bind – 240 sider',
  detail: 'Tall og regning, geometri og måling, brøk og prosent, algebra, funksjoner, vurdering og eksamen'
}, {
  label: '5 bonuser uten ekstra kostnad',
  detail: 'Årsplan, 100 ekstra arbeidsark, Matematikkspill, Problemløsningsbanken og Vikarpakken'
}, {
  label: 'For 1.–10. trinn',
  detail: 'Ferdige økter, arbeidsark i 3 nivåer, fasit og vurderingskriterier'
}];
export const offerIncludes = ['Umiddelbar digital nedlasting', 'PDF-filer du kan lagre', 'Klar til utskrift', 'Ingen fysisk vare sendes'];
export const PRICE_AMOUNT = '299';
export const CURRENCY = 'NOK';
export const PRICE = `${PRICE_AMOUNT}\u00A0${CURRENCY}`;
export const PRICE_BEFORE = '599\u00A0NOK';

// Only add authentic reviews of MATTEKLAR. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Hvordan får jeg materialet?',
  a: 'MATTEKLAR™ leveres digitalt. Etter kjøpet får du tilgang til PDF-filer som du kan laste ned og lagre. Du kan bruke materialet på PC, nettbrett eller mobil og skrive ut de sidene du trenger.'
}, {
  q: 'Er det tilpasset LK20?',
  a: 'Ja. Årsplanen og øktene er koblet til kjerneelementene og kompetansemålene i matematikk for 1.–10. trinn. MATTEKLAR™ er et selvstendig undervisningsmateriell og ikke et offisielt produkt fra Utdanningsdirektoratet.'
}, {
  q: 'Hvilke trinn passer det for?',
  a: 'Materialet dekker 1.–10. trinn: Bind 1 og 2 for 1.–4. trinn, Bind 3 og 4 for 5.–7. trinn og Bind 5 og 6 for 8.–10. trinn. Du velger økter og oppgaver ut fra trinnet og klassen din.'
}, {
  q: 'Kan jeg virkelig forberede en økt på 5 minutter?',
  a: 'MATTEKLAR™ er laget for å gjøre forberedelsene raskere. Du åpner et bind, velger en ferdig økt og leser gjennom den på omtrent fem minutter. Tiden kan variere ut fra økta og behovet for tilpasning.'
}, {
  q: 'Holder materialet for et helt skoleår?',
  a: 'MATTEKLAR™ inneholder en årsplan for 38 skoleuker, ferdige økter og opplegg i alle bindene. Du velger, kombinerer og tilpasser materialet etter din plan og elevenes behov.'
}, {
  q: 'Er det arbeidsark i flere nivåer?',
  a: 'Ja. Arbeidsarkene finnes i 3 nivåer – støtte, basis og utfordring – slik at elever med ulike behov kan jobbe med samme mål.'
}, {
  q: 'Er det fasit?',
  a: 'Ja. Det er fasit til arbeidsarkene, oppgavene og prøvene, slik at du sparer tid på retting.'
}, {
  q: 'Er vurdering og eksamen inkludert?',
  a: 'Ja. Bind 6 inneholder underveisvurdering, vurderingskriterier i 3 nivåer, prøver A/B med fasit, forberedelse til nasjonale prøver i regning og eksamen 10. trinn, og 100 ferdige tilbakemeldinger.'
}, {
  q: 'Må jeg skrive ut alt?',
  a: 'Nei. Du velger selv hvilke sider du skriver ut, og du kan også bruke deler av materialet digitalt.'
}, {
  q: 'Er de fem bonusene inkludert?',
  a: `Ja. Alle fem bonusene er inkludert i pakken uten ekstra kostnad – alt for ${PRICE}.`
}, {
  q: 'Er det et abonnement?',
  a: 'Nei. Du betaler én gang og får tilgang til det digitale materialet. Ingen faste trekk.'
}, {
  q: 'Kan jeg bruke materialet på mobil eller nettbrett?',
  a: 'Ja. PDF-filene kan åpnes på PC, nettbrett og mobil. For å se på arbeidsarkene før utskrift anbefaler vi PC eller nettbrett.'
}, {
  q: 'Finnes det en garanti?',
  a: 'Ja. MATTEKLAR™ har 7 dagers fornøydgaranti. Hvis materialet ikke svarer til forventningene dine, kan du kontakte oss og be om å få pengene tilbake innen garantiperioden, i henhold til vilkårene.'
}];
export const footerLinks = ['Kjøpsvilkår', 'Personvern', 'Refusjon', 'Kontakt'];
